import { combineLatest, Observable, of, OperatorFunction, distinctUntilChanged, map, take, tap } from 'rxjs';
import { Injectable } from '@angular/core';

import { CmsComponent, CmsService, RoutingService } from '@spartacus/core';

import { deepEqualWithoutTime } from '../utils';

@Injectable({
  providedIn: 'root'
})
export class ComponentService {
  constructor(private cmsService: CmsService, private routingService: RoutingService) {}

  getLazyComponentData(
    component: CmsComponent,
    lazyProperty: string,
    mandatoryFunction?: (CmsComponent) => boolean
  ): Observable<CmsComponent> {
    const componentWithLazy = { ...component, [lazyProperty + 'Objects']: [] };
    const componentsData = !!component && !!component[lazyProperty]
      ? component[lazyProperty]
        .split(' ')
        .filter((cmp) => !!cmp)
        .map((cmp) => this.cmsService.getComponentData(cmp))
      : [];
    if (componentsData.length === 0) {
      return of(componentWithLazy);
    }
    return combineLatest(componentsData).pipe(
      tap((lazyComponents: CmsComponent[]) => (
        componentWithLazy[lazyProperty + 'Objects'] = lazyComponents
          .filter((cmp) => (mandatoryFunction ? mandatoryFunction(cmp) : true))
          .map((cmp) => ({ ...cmp }))
      )),
      map((_) => componentWithLazy)
    );
  }

  reloadLazyComponentData(component: CmsComponent, lazyProperty: string): Observable<CmsComponent> {
    return this.cmsService
      .getComponentData(component[lazyProperty]['uid'])
      .pipe(map((lazyComponent) => ({ ...component, [lazyProperty]: lazyComponent })));
  }

  /**
   * This function is used to reload component which contains array attributes (object with item0, item1,... properties)
   * as these attributes are not exposed through the /pages API
   */
  reloadComponentForArrayProperties(properties: string[]): OperatorFunction<CmsComponent, CmsComponent> {
    return (component$) =>
      component$.pipe(
        distinctUntilChanged((previous, current) => deepEqualWithoutTime(previous, current)),
        tap((cmp) => {
          if (properties.every((property) => cmp[property] === undefined)) {
            this.routingService
              .getPageContext()
              .pipe(take(1))
              .subscribe((pageContext) => this.cmsService.refreshComponent(cmp.uid, pageContext));
          }
        })
      );
  }

  getSubComponentsLazyComponentData(
    component: CmsComponent,
    subComponentsProperty: string,
    lazyProperty: string,
    mandatoryFunction?: (CmsComponent) => boolean
  ): Observable<CmsComponent> {
    if (component[subComponentsProperty]?.length > 0) {
      return combineLatest(
        component[subComponentsProperty].map((subCmp) =>
          this.getLazyComponentData(subCmp, lazyProperty, mandatoryFunction)
        )
      ).pipe(map((subCmps) => ({ ...component, [subComponentsProperty]: subCmps })));
    } else {
      return of({ ...component });
    }
  }

  getSubComponentLazyComponentData(
    component: CmsComponent,
    subComponentProperty: string,
    lazyProperty: string,
    mandatoryFunction?: (CmsComponent) => boolean
  ): Observable<CmsComponent> {
    return this.getLazyComponentData(component[subComponentProperty], lazyProperty, mandatoryFunction).pipe(
      map((subCmp) => ({ ...component, [subComponentProperty]: subCmp }))
    );
  }
}
